import { useCurrentUser } from '@cma/app'
import { useMemo } from 'react'
import { usePlatformClient } from '../../../usePlatformClient'
import { OrgOption } from '../types'

export function useOrganizationOptions(
  filterSelected?: boolean,
  selectedTarget?: any,
  mode: 'hierarchy' | 'list' = 'list'
) {
  const { data: { currentUser } = {} } = useCurrentUser()
  const { data: { platformClient } = {} } = usePlatformClient({
    id: currentUser?.account?.id || ''
  })

  const options: OrgOption[] = useMemo(() => {
    if (!platformClient) return []

    const shareableTargetIds = new Set(
      (platformClient?.shareableTargets || []).map(
        (target: any) => `${target.entityType}-${target.entityId}`
      )
    )

    const isOptionEnabled = (type: string, id: string) => {
      const key = `${type}-${id}`
      return shareableTargetIds.has(key)
    }

    const allOptions = [
      {
        label: platformClient?.platformAccountName || '',
        value: {
          type: 'Account',
          id: `p-${platformClient?.id || ''}`,
          usersCount: platformClient?.usersCount || 0,
          brokerageId: null
        },
        isDisabled: !isOptionEnabled('Account', platformClient?.id || '')
      },
      ...(platformClient?.brokerages || [])
        .map((brokerage: any) => ({
          label: brokerage?.name || 'N/A',
          value: {
            type: 'Brokerage',
            id: `b-${brokerage.id}`,
            usersCount: brokerage.usersCount || 0,
            brokerageId: null
          },
          isDisabled: !isOptionEnabled('Brokerage', brokerage.id)
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      ...(platformClient?.offices || []).map((office: any) => ({
        label: office?.name || 'N/A',
        value: {
          type: 'Office',
          id: `o-${office.id}`,
          usersCount: office.usersCount || 0,
          brokerageId: office.brokerageId || null
        },
        isDisabled: !isOptionEnabled('Office', office.id)
      })),
      ...(platformClient?.teams || [])
        .map((team: any) => ({
          label: team?.name || 'N/A',
          value: {
            type: 'Team',
            id: `t-${team.id}`,
            usersCount: team.usersCount || 0,
            brokerageId: team.primaryOffice?.brokerageId || null,
            primaryOfficeId: team.primaryOffice?.id || null
          },
          isDisabled: !isOptionEnabled('Team', team.id)
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ]

    if (filterSelected && selectedTarget) {
      const selected = selectedTarget.contentShares?.map((share: any) => {
        return `${share.shareableType}-${share.shareableId}`
      })

      if (mode === 'hierarchy') {
        // In hierarchy mode, mark selected items as disabled but keep them visible
        return allOptions.map((option) => {
          const optionKey = `${option.value.type}-${
            option.value.id.split('-')[1]
          }`
          const isAlreadySelected = selected?.includes(optionKey)

          return {
            ...option,
            isDisabled: option.isDisabled || isAlreadySelected
          }
        })
      } else {
        // In list mode, filter out selected items completely
        return allOptions.filter((option) => {
          const optionKey = `${option.value.type}-${
            option.value.id.split('-')[1]
          }`
          return !selected?.includes(optionKey)
        })
      }
    }

    return allOptions
  }, [filterSelected, selectedTarget, platformClient, mode])

  return { options, platformClient }
}
